// =====================================================
// Master role-aware sidebar — used in all dashboards
//
// R-NAV-2 (spec §3.2.1–3.2.11): full redesign of all 11 role sidebars to
// link to each role's REAL tools (not mock pages). Mock /instructor +
// /authoring are gone; real surfaces (/admin/course-content, /gradebook,
// /catalog, /my-courses, /tutor, …) are in. Not-yet-built destinations
// (⚫ NEW in the spec) point to ComingSoon placeholders (PLACEHOLDER_ROUTES)
// with the correct title — the nav structure is final; content lands in the
// owning sub-R. Items can carry an optional CASL `can` gate (spec §2.7-4);
// items the active ability forbids are filtered out and groups that end up
// empty are hidden.
//
// Phase-14.5 C4: the SIDEBAR_BY_ROLE structure is a flat list of "header"
// items (`{ h }`) interleaved with "link" items (`{ id, t, ic, can? }`).
// =====================================================
import React from "react";
import { Icon } from "./icons";
import { useRole, type RoleId } from "./role";
import { useAbility } from "./auth/Can";
import { useChatUnread } from "./hooks/useChatUnread";
import type { Actions, Subjects, AppAbility } from "./auth/ability";
import type { Go } from "./router";
import { useTranslation } from "react-i18next";
import { toFaDigits } from "./i18n/jalali";

// R-CHAT M4 — digit helper for the messages badge. S-I18N-W1a replaced the
// local Persian-only table with the shared locale-aware one, so an Arabic
// reader sees ٣ and not ۳ (two different codepoints — STYLE_AR §۳).

interface SidebarHeader {
  h: string;
}
export interface SidebarLink {
  /** Route key `go(...)` navigates to (1- or 2-segment, e.g. "admin/programs"). */
  id: string;
  t: string;
  ic: string;
  /** Optional CASL gate — item hidden unless ability.can(action, subject). */
  can?: { action: Actions; subject: Subjects };
}
type SidebarEntry = SidebarHeader | SidebarLink;

const isHeader = (e: SidebarEntry): e is SidebarHeader =>
  (e as SidebarHeader).h !== undefined;

// CASL gate reused on every "audit" link (spec §2.6: /audit → read:AuditLog).
const CAN_AUDIT = { action: "read", subject: "AuditLog" } as const;

// Admin academic/people/content/system/governance groups. Extracted so the
// super_admin sidebar can reuse the full set (spec §3.2.5: "همه‌ی گروه‌های
// مدیر سیستم … عیناً مثل ۳.۲.۴") without duplicating it.
const ADMIN_SIDEBAR: SidebarEntry[] = [
  // ساختار آکادمیک — strictly top-down so the hierarchy is self-evident:
  // دانشکده → بخش → گروه آموزشی → رشته → گرایش → چارت درسی. (D-2026-06-14:
  // relabelled per owner — «هیأت‌ها» collided with هیئت علمی and «برنامه‌ها»
  // was unclear; درس/پیش‌نیاز/عمومی-تخصصی are inside چارت درسی, spelled out.)
  { h: "nav.group.academicStructure" },
  { id: "admin", t: "nav.item.admin", ic: "home" },
  { id: "admin/schools", t: "nav.item.admin-schools", ic: "grad" },
  { id: "admin/faculties", t: "nav.item.admin-faculties", ic: "layers" },
  { id: "admin/departments", t: "nav.item.admin-departments", ic: "folder" },
  { id: "admin/programs", t: "nav.item.admin-programs", ic: "cert" },
  { id: "admin/specializations", t: "nav.item.admin-specializations", ic: "target" },
  { id: "curriculum-builder", t: "nav.item.curriculum-builder", ic: "book" },
  { id: "faculty", t: "nav.item.faculty", ic: "users" },
  { id: "admin/offerings", t: "nav.item.admin-offerings", ic: "live" },
  // S-LIVE-P1-W1 — سقفِ ظرفیتِ کلاسِ زنده (کلاس‌های هم‌زمان/شرکت‌کنندگان/کاشی‌های گالری).
  { id: "live-class/admin", t: "nav.item.live-class-admin", ic: "settings" },
  { id: "admin/reference-data", t: "nav.item.admin-reference-data", ic: "layers" },
  { id: "admin/ministry-library", t: "nav.item.admin-ministry-library", ic: "book" },
  { id: "admin/cohorts", t: "nav.item.admin-cohorts", ic: "users" },
  { h: "nav.group.people" },
  { id: "admin/people", t: "nav.item.admin-people", ic: "users" },
  { id: "network", t: "nav.item.network", ic: "users" },
  { id: "admin/student-overview", t: "nav.item.admin-student-overview", ic: "file" },
  { id: "admin/profiles", t: "nav.item.admin-profiles", ic: "user" },
  { id: "admin/students", t: "nav.item.admin-students", ic: "users" },
  { id: "admin/instructors", t: "nav.item.admin-instructors", ic: "grad" },
  { id: "admin/users", t: "nav.item.admin-users", ic: "users" },
  // R-MON W4 — connect a real والد to a دانشجو (runtime onboarding; was seed-only).
  { id: "admin/parent-links", t: "nav.item.admin-parent-links", ic: "link" },
  { id: "admin/roles", t: "nav.item.admin-roles", ic: "shield" },
  { id: "admin/applications", t: "nav.item.admin-applications", ic: "inbox" },
  { id: "admin/hr", t: "nav.item.admin-hr", ic: "file" },
  { id: "admin/enrollments", t: "nav.item.admin-enrollments", ic: "cert" },
  { h: "nav.group.content" },
  { id: "admin/course-content", t: "nav.item.admin-course-content", ic: "sparkle" },
  { id: "library", t: "nav.item.library", ic: "folder" },
  { h: "nav.group.system" },
  { id: "admin/terms", t: "nav.item.admin-terms", ic: "calendar" },
  { id: "admin/degree-policies", t: "nav.item.admin-degree-policies", ic: "shield" },
  { id: "admin/regulation", t: "nav.item.admin-regulation", ic: "shield" },
  { id: "admin/commission", t: "nav.item.admin-commission", ic: "shield" },
  { id: "admin/stage-progress", t: "nav.item.admin-stage-progress", ic: "target" },
  // S-ASSESS-W3-UX — تا امروز کانونِ سنجش فقط در منویِ assessor/assessment_manager
  // بود و ادمین هیچ راهی برایِ پیداکردنِ آن نداشت (مالک نتوانست صفحه را بیابد).
  // هر سه نقطهٔ‌ورودِ زنجیره اینجا می‌آید تا مسیرِ کلیک از منو کامل باشد.
  { id: "admin/assess-center", t: "nav.item.admin-assess-center", ic: "target" },
  { id: "assess-center", t: "nav.item.assess-center", ic: "check" },
  { id: "assess-center/my", t: "nav.item.assess-center-my", ic: "cert" },
  // S-ASSESS-W5b — همان دلیلِ کامنتِ بالا: ادمین باید بدونِ اتکا به اینجکشنِ
  // پویایِ buildAssessCenterEntries (که فقط در buildSidebarGroups ادغام می‌شود
  // و در تستِ استاتیکِ SIDEBAR_BY_ROLE دیده نمی‌شود) مستقیماً به گزارشِ ر۱ برسد.
  { id: "assess-center/my-report", t: "nav.item.assess-center-my-report", ic: "file" },
  { id: "admin/workflows", t: "nav.item.admin-workflows", ic: "inbox" },
  { id: "admin/calendar", t: "nav.item.admin-calendar", ic: "calendar" },
  // R-COMPLY — مدیریتِ مالی (شهریه/حقوق/خزانه/احکام/حق‌التدریس) کارِ «مدیر مالی» است،
  // نه ریاست دانشگاه. این آیتم‌ها از منوی ادمین برداشته شدند و به منوی finance_manager
  // (و نظارتیِ super_admin) منتقل شدند. «پلن‌ها و قیمت‌ها» (قیمت‌گذاری محصول) با ادمین می‌ماند.
  { id: "admin/plans", t: "nav.item.admin-plans", ic: "dollar" },
  { id: "admin/integrations", t: "nav.item.admin-integrations", ic: "chip" },
  { id: "admin/notif-templates", t: "nav.item.admin-notif-templates", ic: "bell" },
  { id: "admin/broadcast", t: "nav.item.admin-broadcast", ic: "bell" },
  { h: "nav.group.governance" },
  { id: "audit", t: "nav.item.audit", ic: "shield", can: CAN_AUDIT },
  { id: "ai-governance", t: "nav.item.ai-governance", ic: "sparkle" },
  // S-AI-PROVIDER-1 — مصرف/هزینهٔ AI (فقط‌خواندنی، ادمین‌محور).
  { id: "admin/ai-usage", t: "nav.item.admin-ai-usage", ic: "chart" },
  // R-MON W12/W13 — real system-health console (counts + Node runtime depth).
  { id: "monitoring", t: "nav.item.monitoring", ic: "pulse" },
  // S-MEDIA-UP / S-OWNER-VIS step3 — کدام قابلیت‌های اختیاری فعال/خاموش/منتظرند.
  { id: "capabilities-status", t: "nav.item.capabilities-status", ic: "flag" },
  { id: "analytics-admin", t: "nav.item.analytics-admin", ic: "chart" },
  { id: "settings", t: "nav.item.settings", ic: "settings" },
];

export const SIDEBAR_BY_ROLE: Record<RoleId, SidebarEntry[]> = {
  // ===== §3.2.1 Student =====
  student: [
    { h: "nav.group.learning" },
    // homeRoute is "progress" (the live learner page the student lands on after
    // login); the sidebar "میز کار" must point there too so it highlights on
    // landing and doesn't divert to the mock /dashboard.
    { id: "progress", t: "nav.item.progress", ic: "home" },
    { id: "my-courses", t: "nav.item.my-courses", ic: "book" },
    { id: "catalog", t: "nav.item.catalog", ic: "grad" },
    { id: "registration", t: "nav.item.registration", ic: "plus" },
    { id: "add-drop", t: "nav.item.add-drop", ic: "check" },
    { id: "schedule", t: "nav.item.schedule", ic: "calendar" },
    { id: "calendar", t: "nav.item.calendar", ic: "calendar" },
    { id: "assignments", t: "nav.item.assignments", ic: "check" },
    { id: "transcript", t: "nav.item.transcript", ic: "file" },
    { id: "my-grades", t: "nav.item.my-grades", ic: "check" },
    { id: "my-attendance", t: "nav.item.my-attendance", ic: "shield" },
    { id: "degree-audit", t: "nav.item.degree-audit", ic: "target" },
    { id: "my-chart", t: "nav.item.my-chart", ic: "grad" },
    // R-MED — stage-based (پزشکی/دندان/دارو) students; page self-gates for others.
    { id: "my-path", t: "nav.item.my-path", ic: "target" },
    { h: "nav.group.classAndInteraction" },
    { id: "live-class", t: "nav.item.live-class", ic: "live" },
    { id: "recordings", t: "nav.item.recordings", ic: "video" },
    { id: "community", t: "nav.item.community", ic: "users" },
    { id: "messages", t: "nav.item.messages", ic: "chat" },
    // R-CONNECT — «شبکهٔ من» (ارتباط‌ها/دنبال‌کننده‌ها). Pairs with پیام‌ها; the
    // pendingIncoming badge lives on the global nav chip (shared.tsx Nav).
    { id: "network", t: "nav.item.network", ic: "users" },
    { id: "officehours", t: "nav.item.officehours", ic: "headset" },
    { h: "nav.group.ai" },
    // S-19 — ONE assistant destination. The old «دستیار هوشمند» (/tutor) was a
    // near-duplicate twin of this; the RAG tutor is now the «پرسش از جزوه‌ها» kind
    // INSIDE /agents, and /tutor redirects here. (owner complaint #6: two places.)
    { id: "agents", t: "nav.item.agents", ic: "bot" },
    { id: "cognitive-profile", t: "nav.item.cognitive-profile", ic: "chip" },
    { id: "adaptive-path", t: "nav.item.adaptive-path", ic: "target" },
    { h: "nav.group.studentServices" },
    { id: "service-desk", t: "nav.item.service-desk", ic: "inbox" },
    // Support ticketing — anyone can open a ticket; the page shows the requester
    // their own tickets + a "new ticket" form, and پشتیبانی reviews them in
    // «صف تیکت‌ها». This entry was missing for end users (owner-reported).
    { id: "tickets", t: "nav.item.tickets", ic: "headset" },
    { id: "wallet", t: "nav.item.wallet", ic: "dollar" },
    { id: "tuition", t: "nav.item.tuition", ic: "dollar" },
    // Finance pages that were only reachable via in-page buttons on /tuition —
    // surfaced as direct sidebar items so every built feature is nav-reachable.
    { id: "tuition/loans", t: "nav.item.tuition-loans", ic: "dollar" },
    { id: "tuition/refund", t: "nav.item.tuition-refund", ic: "dollar" },
    { id: "tuition/clearance", t: "nav.item.tuition-clearance", ic: "check" },
    { id: "library", t: "nav.item.library", ic: "folder" },
    { id: "labs", t: "nav.item.labs", ic: "flask" },
    { id: "credential", t: "nav.item.credential", ic: "cert" },
    { id: "career", t: "nav.item.career", ic: "bolt" },
    { id: "financial-aid", t: "nav.item.financial-aid", ic: "dollar" },
    // سلامت و مشاوره — the wellness hub. The validated psychology tests live
    // INSIDE this tab now (the page links to /wellness/tests prominently), so the
    // standalone «تست‌های روان‌شناسی» nav item was removed (owner: keep it in the
    // same tab, not a separate silo).
    { id: "wellness", t: "nav.item.wellness", ic: "shield" },
    // S-ASSESS-CENTER (W2) — سنجش شایستگی: a DISTINCT catalogue from wellness
    // self-tests (competency/skills assessment, not psychological screening).
    { id: "assess", t: "nav.item.assess", ic: "target" },
    { h: "nav.group.account" },
    { id: "profile", t: "nav.item.profile", ic: "user" },
    { id: "achievements", t: "nav.item.achievements", ic: "trophy" },
    { id: "portfolio", t: "nav.item.portfolio", ic: "star" },
    { id: "settings", t: "nav.item.settings-alt2", ic: "settings" },
  ],

  // ===== §3.2.2 Instructor =====
  instructor: [
    { h: "nav.group.teaching" },
    { id: "instructor-dashboard", t: "nav.item.instructor-dashboard", ic: "home" },
    { id: "my-courses", t: "nav.item.my-courses", ic: "book" },
    { id: "admin/course-content", t: "nav.item.admin-course-content-alt2", ic: "sparkle" },
    // R-MARKET P1 — «بازار دانش» authoring studio (course-building skeleton).
    { id: "market/studio", t: "nav.item.market-studio", ic: "video" },
    { id: "library", t: "nav.item.library-alt2", ic: "folder" },
    { id: "officehours", t: "nav.item.officehours-alt2", ic: "headset" },
    { id: "calendar", t: "nav.item.calendar-alt2", ic: "calendar" },
    { h: "nav.group.assessment" },
    { id: "gradebook", t: "nav.item.gradebook", ic: "check" },
    { id: "grading-queue", t: "nav.item.grading-queue", ic: "file" },
    { id: "question-bank", t: "nav.item.question-bank", ic: "chip" },
    { id: "assessment-builder", t: "nav.item.assessment-builder", ic: "sparkle" },
    { id: "rubric-builder", t: "nav.item.rubric-builder", ic: "target" },
    { id: "grade-submission", t: "nav.item.grade-submission", ic: "file" },
    { h: "nav.group.students" },
    { id: "roster", t: "nav.item.roster", ic: "users" },
    { id: "attendance", t: "nav.item.attendance", ic: "check" },
    { id: "class-analytics", t: "nav.item.class-analytics", ic: "chart" },
    { id: "item-analysis", t: "nav.item.item-analysis", ic: "chart" },
    { h: "nav.group.classAndAi" },
    { id: "live-class", t: "nav.item.live-class-alt2", ic: "live" },
    { id: "copilot", t: "nav.item.copilot", ic: "sparkle" },
    { id: "messages", t: "nav.item.messages", ic: "chat" },
    { id: "network", t: "nav.item.network", ic: "users" },
    { id: "tickets", t: "nav.item.tickets", ic: "headset" },
    // R2-088 — the instructor is a real approver: `stepScopeOk` (service-desk.
    // service.ts:84-99) supports the `instructor` step and `listPendingApproval`
    // builds their queue, but this block listed no way in. `/service-desk` is
    // keyless, so the instructor could reach it by typing the URL — that made it
    // a discovery dead end, not an access block: an approval nobody knows is
    // waiting for them never happens, and the student's request stalls silently.
    { id: "service-desk", t: "nav.item.service-desk-alt2", ic: "inbox" },
    { h: "nav.group.finance" },
    { id: "grade-submission", t: "nav.item.grade-submission", ic: "file" },
    { id: "payroll/claims", t: "nav.item.payroll-claims", ic: "dollar" },
    { id: "payroll/my-payslips", t: "nav.item.payroll-my-payslips", ic: "dollar" },
    { h: "nav.group.account" },
    { id: "profile", t: "nav.item.profile", ic: "user" },
    { id: "settings", t: "nav.item.settings-alt2", ic: "settings" },
  ],

  // ===== §3.2.3 Faculty admin / ریاست دانشکده (scope-bound to own faculty) =====
  faculty_admin: [
    { h: "nav.group.facultyStructure" },
    { id: "faculty-dashboard", t: "nav.item.faculty-dashboard", ic: "home" },
    { id: "admin/departments", t: "nav.item.admin-departments", ic: "folder" },
    { id: "admin/programs", t: "nav.item.admin-programs", ic: "cert" },
    { id: "admin/specializations", t: "nav.item.admin-specializations", ic: "target" },
    { id: "curriculum-builder", t: "nav.item.curriculum-builder", ic: "book" },
    { id: "faculty", t: "nav.item.faculty", ic: "users" },
    { h: "nav.group.education" },
    { id: "admin/courses", t: "nav.item.admin-courses", ic: "book" },
    { id: "admin/offerings", t: "nav.item.admin-offerings-alt2", ic: "live" },
    { id: "instructor-assignment", t: "nav.item.instructor-assignment", ic: "users" },
    { h: "nav.group.studentAffairs" },
    { id: "admin/student-overview", t: "nav.item.admin-student-overview", ic: "file" },
    { id: "admin/hr", t: "nav.item.admin-hr-alt2", ic: "file" },
    { id: "probation-review", t: "nav.item.probation-review", ic: "shield" },
    { id: "admin/commission", t: "nav.item.admin-commission", ic: "file" },
    // R-MED — stage-based programs (پزشکی/دندان/دارو): the dean records/reads
    // students' stage progression + reviews the stage structure (read-only CRUD).
    { id: "admin/stage-progress", t: "nav.item.admin-stage-progress", ic: "target" },
    { id: "service-desk", t: "nav.item.service-desk-alt2", ic: "inbox" },
    { h: "nav.group.reports" },
    { id: "faculty-reports", t: "nav.item.faculty-reports", ic: "chart" },
    { id: "inbox", t: "nav.item.inbox", ic: "bell" },
    { id: "messages", t: "nav.item.messages", ic: "chat" },
    { id: "network", t: "nav.item.network", ic: "users" },
    { id: "settings", t: "nav.item.settings-alt2", ic: "settings" },
  ],

  // ===== §3.2.4 System admin =====
  admin: ADMIN_SIDEBAR,

  // ===== §3.2.5 Super admin = super tools + all admin groups + global config =====
  // R-MON W12 (honesty): the production deployment is SINGLE-TENANT (one
  // university), so the platform-ops MOCKS «جعل هویت» (impersonate) and «پیکربندی
  // چندمستأجری» (multitenancy) — DemoBadge pages with no backend — are no longer
  // advertised in the nav (the page files stay, just unlinked). «سلامت سامانه»
  // (system-health) is the real backbone-observability console and rides in via
  // ADMIN_SIDEBAR below, so the super_admin-local duplicate is dropped too.
  super_admin: [
    { h: "nav.group.superAdmin" },
    { id: "super", t: "nav.item.super", ic: "home" },
    { id: "super/tenants", t: "nav.item.super-tenants", ic: "grad" },
    ...ADMIN_SIDEBAR,
    // Break-glass finance oversight (the day-to-day finance owner is مدیر مالی).
    { h: "nav.group.financeOversight" },
    { id: "admin/finance/treasury", t: "nav.item.admin-finance-treasury", ic: "chart" },
    { id: "admin/finance/payroll", t: "nav.item.admin-finance-payroll", ic: "dollar" },
    { id: "admin/finance/decrees", t: "nav.item.admin-finance-decrees", ic: "dollar" },
    { id: "admin/finance/claims", t: "nav.item.admin-finance-claims", ic: "check" },
    { id: "admin/finance/payroll-config", t: "nav.item.admin-finance-payroll-config", ic: "dollar" },
    { id: "admin/finance/withdrawals", t: "nav.item.admin-finance-withdrawals", ic: "dollar" },
    { id: "admin/tuition-config", t: "nav.item.admin-tuition-config", ic: "dollar" },
    // S-FIX-FINANCE-BUNDLE-1 (R2-Bzz) — تأییدِ تسویهٔ فاکتورِ سازمانی و سقفِ
    // صندلی اندپوینتِ مالی دارند، ولی هیچ ورودیِ منویی نداشتند: صفحه فقط با
    // تایپِ دستیِ URL پیدا می‌شد. کلیدها از پیش در shell.json هستند.
    { id: "org/invoices", t: "nav.item.org-invoices", ic: "dollar" },
    { id: "org/seats", t: "nav.item.org-seats", ic: "check" },
  ],

  // ===== §3.2.6 Parent / guardian =====
  parent: [
    { h: "nav.group.child" },
    { id: "parent", t: "nav.item.parent", ic: "home" },
    { id: "parent/grades", t: "nav.item.parent-grades", ic: "file" },
    { id: "parent/attendance", t: "nav.item.parent-attendance", ic: "check" },
    { id: "calendar", t: "nav.item.calendar-alt3", ic: "calendar" },
    { h: "nav.group.communication" },
    { id: "inbox", t: "nav.item.inbox", ic: "bell" },
    { id: "messages", t: "nav.item.messages-alt2", ic: "chat" },
    { id: "network", t: "nav.item.network", ic: "users" },
    { id: "officehours", t: "nav.item.officehours-alt3", ic: "headset" },
    { id: "tickets", t: "nav.item.tickets", ic: "headset" },
    { h: "nav.group.finance" },
    { id: "parent/tuition", t: "nav.item.parent-tuition", ic: "dollar" },
    { id: "settings", t: "nav.item.settings-alt2", ic: "settings" },
  ],

  // ===== §3.2.7 Teaching assistant (staff) — R-MON W7 =====
  // The TA now has a real section link (SectionAssistant), so its workspace is
  // its ASSIGNED sections only (server 404s elsewhere): roster + class/item
  // analytics (read) + DRAFT grade entry on those sections. FINALIZE (قطعی) stays
  // instructor/admin-only, so «ثبت نمرات» is labelled «ثبت نمرهٔ پیش‌نویس» here and
  // hides the finalize action for a TA.
  ta: [
    { h: "nav.group.teachingAssistance" },
    { id: "ta", t: "nav.item.ta", ic: "home" },
    { h: "nav.group.assessmentAssigned" },
    { id: "roster", t: "nav.item.roster", ic: "users" },
    { id: "grade-submission", t: "nav.item.grade-submission-alt2", ic: "file" },
    { id: "gradebook", t: "nav.item.gradebook", ic: "check" },
    { id: "class-analytics", t: "nav.item.class-analytics", ic: "chart" },
    { id: "item-analysis", t: "nav.item.item-analysis-alt2", ic: "chart" },
    { id: "officehours", t: "nav.item.officehours-alt2", ic: "headset" },
    { id: "calendar", t: "nav.item.calendar-alt2", ic: "calendar" },
    { h: "nav.group.liveClass" },
    { id: "live-class", t: "nav.item.live-class-alt3", ic: "live" },
    { id: "messages", t: "nav.item.messages", ic: "chat" },
    { id: "network", t: "nav.item.network", ic: "users" },
    { h: "nav.group.finance" },
    { id: "payroll/my-payslips", t: "nav.item.payroll-my-payslips", ic: "dollar" },
    { h: "nav.group.account" },
    { id: "profile", t: "nav.item.profile", ic: "user" },
    { id: "settings", t: "nav.item.settings-alt2", ic: "settings" },
  ],

  // ===== §3.2.8 Content manager =====
  content_manager: [
    { h: "nav.group.content" },
    { id: "content", t: "nav.item.content", ic: "home" },
    { id: "admin/course-content", t: "nav.item.admin-course-content-alt3", ic: "sparkle" },
    // R-MARKET P1 — «بازار دانش» authoring studio (course-building skeleton).
    { id: "market/studio", t: "nav.item.market-studio-alt2", ic: "video" },
    // R-MARKET P3 — «بازار دانش» review queue (content_manager = the reviewer §1.2).
    { id: "market/review", t: "nav.item.market-review", ic: "check" },
    { id: "content-review", t: "nav.item.content-review", ic: "check" },
    { id: "my-courses", t: "nav.item.my-courses-alt2", ic: "book" },
    { id: "library", t: "nav.item.library-alt2", ic: "folder" },
    { id: "messages", t: "nav.item.messages", ic: "chat" },
    { id: "network", t: "nav.item.network", ic: "users" },
    { h: "nav.group.analytics" },
    { id: "content-analytics", t: "nav.item.content-analytics", ic: "chart" },
    { h: "nav.group.finance" },
    { id: "payroll/my-payslips", t: "nav.item.payroll-my-payslips", ic: "dollar" },
    { h: "nav.group.account" },
    { id: "profile", t: "nav.item.profile", ic: "user" },
    { id: "settings", t: "nav.item.settings-alt2", ic: "settings" },
  ],

  // ===== §3.2.9 Support =====
  support: [
    { h: "nav.group.support" },
    { id: "support", t: "nav.item.support", ic: "home" },
    { id: "tickets", t: "nav.item.tickets-alt2", ic: "inbox" },
    { id: "user-lookup", t: "nav.item.user-lookup", ic: "search" },
    { id: "audit", t: "nav.item.audit", ic: "shield", can: CAN_AUDIT },
    { id: "messages", t: "nav.item.messages", ic: "chat" },
    { id: "network", t: "nav.item.network", ic: "users" },
    { id: "inbox", t: "nav.item.inbox", ic: "bell" },
    { h: "nav.group.finance" },
    { id: "payroll/my-payslips", t: "nav.item.payroll-my-payslips", ic: "dollar" },
    { h: "nav.group.account" },
    { id: "profile", t: "nav.item.profile", ic: "user" },
    { id: "settings", t: "nav.item.settings-alt2", ic: "settings" },
  ],

  // ===== §3.2.10 Moderator =====
  moderator: [
    { h: "nav.group.supervision" },
    { id: "moderate", t: "nav.item.moderate", ic: "home" },
    { id: "flagged-reports", t: "nav.item.flagged-reports", ic: "shield" },
    { id: "community-moderation", t: "nav.item.community-moderation", ic: "users" },
    { id: "auto-rules", t: "nav.item.auto-rules", ic: "chip" },
    { id: "moderation-log", t: "nav.item.moderation-log", ic: "file" },
    { h: "nav.group.finance" },
    { id: "payroll/my-payslips", t: "nav.item.payroll-my-payslips", ic: "dollar" },
    { h: "nav.group.account" },
    { id: "profile", t: "nav.item.profile", ic: "user" },
    { id: "settings", t: "nav.item.settings-alt2", ic: "settings" },
  ],

  // ===== Finance manager (مدیر مالی) — payroll/treasury/decree workspace =====
  finance_manager: [
    { h: "nav.group.finance" },
    // خزانه و گزارش‌ها now also hosts the receivable-side people-360 (R-MON W5):
    // a billing roster (search by name/code + status) → finance-360 detail panel,
    // plus the tuition-receivables report — all on the same Treasury page.
    { id: "admin/finance/treasury", t: "nav.item.admin-finance-treasury", ic: "dollar" },
    { id: "admin/finance/payroll", t: "nav.item.admin-finance-payroll", ic: "dollar" },
    { id: "admin/finance/decrees", t: "nav.item.admin-finance-decrees", ic: "file" },
    { id: "admin/finance/claims", t: "nav.item.admin-finance-claims", ic: "check" },
    { id: "admin/finance/payroll-config", t: "nav.item.admin-finance-payroll-config", ic: "settings" },
    { id: "admin/finance/withdrawals", t: "nav.item.admin-finance-withdrawals", ic: "dollar" },
    // S-18 — the contracts + salary-assignment section (issue the حکم from a hire).
    { id: "admin/hr", t: "nav.item.admin-hr-alt3", ic: "file" },
    { h: "nav.group.tuition" },
    { id: "admin/tuition-config", t: "nav.item.admin-tuition-config", ic: "dollar" },
    // S-CARD W0-cfg-b گامِ ۲ / W5 — تنظیماتِ عضویت (بخشِ پول را همین نقش می‌نویسد)
    // + «عضویتِ من» (finance_manager از اینجکشنِ سراسریِ buildMembershipEntries
    // مستثنا است چون خودش همین‌جا هدرِ «عضویت» را دارد؛ رجوع به MEMBERSHIP_STAFF_ROLES).
    { h: "nav.group.membership" },
    { id: "membership", t: "nav.item.membership", ic: "cert" },
    { id: "membership/directory", t: "nav.item.membership-directory", ic: "users" },
    { id: "admin/membership-config", t: "nav.item.admin-membership-config", ic: "settings" },
    // R-MON W5 — the review queues مدیر مالی OWNS (loan/scholarship/refund
    // approve/reject endpoints are @Roles("finance_manager","super_admin")). These
    // pages role-switch to the REVIEW view for finance_manager; the links were
    // missing so the queues were orphaned.
    { h: "nav.group.welfareFund" },
    { id: "tuition/loans", t: "nav.item.tuition-loans", ic: "dollar" },
    { id: "financial-aid", t: "nav.item.financial-aid-alt2", ic: "dollar" },
    { id: "tuition/refund", t: "nav.item.tuition-refund", ic: "dollar" },
    // S-FIX-FINANCE-BUNDLE-1 (R2-Bzz) — دو صفحه‌ای که API‌شان همین نقش را
    // به‌عنوان مجریِ پول می‌شناسد (settlement-confirm/reject و نوشتنِ سقفِ
    // صندلی). بدونِ این دو ردیف، مدیرِ مالی مسیرِ کلیکی به عملِ خودش نداشت.
    { h: "nav.group.organization" },
    { id: "org/invoices", t: "nav.item.org-invoices", ic: "dollar" },
    // S-FIX-FINANCE-2 بندِ ۴ — سقفِ صندلیِ قرارداد را فقط همین نقش می‌نویسد
    // (`PUT /v1/org/seat-cap`)؛ تا این بسته هیچ ورودیِ منویی نداشت.
    { id: "org/seats", t: "nav.item.org-seats", ic: "check" },
    { h: "nav.group.account" },
    { id: "profile", t: "nav.item.profile", ic: "user" },
    { id: "settings", t: "nav.item.settings-alt2", ic: "settings" },
  ],

  // ===== WELL-4 Counselor (مشاور دانشگاه) — least-privilege wellness desk =====
  // ONLY counselor surfaces: own inbox, appointment queue, the tenant's open
  // wellness crisis queue (duty-of-care), profile, settings. Nothing academic/
  // finance. The crisis queue + a scoped student wellness summary are gated +
  // scoped server-side (own-appointment / open-alert students only).
  counselor: [
    { h: "nav.group.advisorDesk" },
    { id: "counselor-inbox", t: "nav.item.counselor-inbox", ic: "home" },
    { id: "counseling-appointments", t: "nav.item.counseling-appointments", ic: "calendar" },
    { id: "crisis-queue", t: "nav.item.crisis-queue", ic: "shield" },
    // R-MON W9 — caseload / monitoring: aggregate roll-up + roster + per-student
    // continuity timeline + schedule-follow-up. All scoped to the counselor's own
    // caseload + linked students server-side.
    { id: "counselor-caseload", t: "nav.item.counselor-caseload", ic: "users" },
    { h: "nav.group.account" },
    { id: "profile", t: "nav.item.profile", ic: "user" },
    { id: "settings", t: "nav.item.settings-alt2", ic: "settings" },
  ],

  // ===== S-CARD فاز A W1/W0-cfg-b — میز عضویت (اپراتور / مدیر برنامه) =====
  // میزِ عملیاتیِ عضویت (صفِ رسیدگی/۳۶۰) هنوز در W5 می‌آید — اینجا فقط «حساب».
  // membership_manager از W0-cfg-b گامِ ۲ صاحبِ صفحهٔ تنظیماتِ سراسریِ عضویت است
  // (این تنظیماتِ محصول است، نه میزِ عملیاتیِ W5)؛ سیاهه‌ای از لینک‌های مرده یا
  // لینک‌هایی که دروازهٔ نقشِ خودشان ردشان می‌کند ساخته نمی‌شود (tests/nav/nav-coherence).
  membership_operator: [
    { h: "nav.group.membership" },
    { id: "membership", t: "nav.item.membership", ic: "cert" },
    { id: "membership/directory", t: "nav.item.membership-directory", ic: "users" },
    { id: "admin/membership-queue", t: "nav.item.admin-membership-queue", ic: "check" },
    { h: "nav.group.account" },
    { id: "profile", t: "nav.item.profile", ic: "user" },
    { id: "settings", t: "nav.item.settings-alt2", ic: "settings" },
  ],
  membership_manager: [
    { h: "nav.group.membership" },
    { id: "membership", t: "nav.item.membership", ic: "cert" },
    { id: "membership/directory", t: "nav.item.membership-directory", ic: "users" },
    { id: "admin/membership-queue", t: "nav.item.admin-membership-queue", ic: "check" },
    { id: "admin/membership-config", t: "nav.item.admin-membership-config", ic: "settings" },
    { h: "nav.group.account" },
    { id: "profile", t: "nav.item.profile", ic: "user" },
    { id: "settings", t: "nav.item.settings-alt2", ic: "settings" },
  ],
  // S-ASSESS-W3b — کنسولِ ارزیاب اکنون مسیرِ فرانت دارد (الگویِ membership_operator بالا).
  assessor: [
    { h: "nav.group.evaluationCenter" },
    { id: "assess-center", t: "nav.item.assess-center-alt2", ic: "check" },
    { id: "assess-center/my", t: "nav.item.assess-center-my-alt2", ic: "cert" },
    { id: "assess-center/my-report", t: "nav.item.assess-center-my-report-alt2", ic: "file" },
    { h: "nav.group.account" },
    { id: "profile", t: "nav.item.profile", ic: "user" },
    { id: "settings", t: "nav.item.settings-alt2", ic: "settings" },
  ],
  assessment_manager: [
    { h: "nav.group.evaluationCenter" },
    { id: "assess-center", t: "nav.item.assess-center-alt2", ic: "check" },
    { id: "admin/assess-center", t: "nav.item.admin-assess-center", ic: "settings" },
    { id: "assess-center/my", t: "nav.item.assess-center-my-alt2", ic: "cert" },
    { id: "assess-center/my-report", t: "nav.item.assess-center-my-report-alt2", ic: "file" },
    { h: "nav.group.account" },
    { id: "profile", t: "nav.item.profile", ic: "user" },
    { id: "settings", t: "nav.item.settings-alt2", ic: "settings" },
  ],

  // ===== §3.2.11 Organisation =====
  org: [
    { h: "nav.group.organization" },
    { id: "org", t: "nav.item.org", ic: "home" },
    { id: "org/users", t: "nav.item.org-users", ic: "users" },
    { id: "org/bulk-enroll", t: "nav.item.org-bulk-enroll", ic: "plus" },
    { id: "org/seats", t: "nav.item.org-seats", ic: "check" },
    { id: "org/invoices", t: "nav.item.org-invoices", ic: "dollar" },
    { id: "org/reports", t: "nav.item.org-reports", ic: "chart" },
    { h: "nav.group.account" },
    { id: "settings", t: "nav.item.settings-alt3", ic: "settings" },
  ],
};

// Wrap a list in <ul> if it isn't already (compat with existing CSS).
// Items are real <a href> + onClick so they: show their destination on hover,
// support Cmd/middle-click → background tab, expose aria-current, and are
// keyboard-reachable. href is "/route" (BrowserRouter).
export interface RoleSideNavProps {
  active: string;
  go: Go;
  /**
   * R7.12 — dual-mode rendering for the Mini-variant Drawer.
   * - "expanded" (default): icon + label, group headers visible.
   * - "mini": icon-only, group headers hidden; label via title= + sr-only span.
   */
  mode?: "mini" | "expanded";
}

export interface SidebarGroup {
  h: string;
  items: SidebarLink[];
}

// R-MARKET P4/P5 — «بازار دانش» group, injected into EVERY role's sidebar via
// buildSidebarGroups so it is uniformly discoverable without editing all 13 role
// arrays. The bare /market catalog (PUBLIC) + «دوره‌های من» (buyer shelf, any authed
// role) show for everyone; «درآمد من» (author) + «مدیریت بازار» (admin) are appended
// only for the roles that own them. The authoring «استودیوی دانش» (/market/studio) +
// reviewer «بازبینی» (/market/review) entries stay role-specific in SIDEBAR_BY_ROLE.
const MARKET_AUTHOR_ROLES: ReadonlySet<RoleId> = new Set([
  "instructor",
  "content_manager",
  "admin",
  "super_admin",
]);
const MARKET_ADMIN_ROLES: ReadonlySet<RoleId> = new Set(["admin", "super_admin"]);

const buildMarketEntries = (roleId: RoleId): SidebarEntry[] => {
  const entries: SidebarEntry[] = [
    { h: "nav.group.knowledgeMarket" },
    { id: "market", t: "nav.item.market", ic: "grad" },
    // S-MKT-PRO-W9 — the buyer's cart; every authed role may buy a course.
    { id: "market/cart", t: "nav.item.market-cart", ic: "cart" },
    // Buyer shelf — every authed role may have purchases (doc §6).
    { id: "market/my-courses", t: "nav.item.market-my-courses", ic: "book" },
    // R-MARKET EXT — the buyer's wishlist; every authed role may save a course.
    { id: "market/wishlist", t: "nav.item.market-wishlist", ic: "heart" },
    // S-MKT-PRO-W4 — purchase history + receipts; same audience as «دوره‌های من».
    { id: "market/purchases", t: "nav.item.market-purchases", ic: "file" },
    // S-MKT-PRO-W8 — the merged money timeline; same audience again (the page itself hides
    // the sections the caller has no money in, driven by the server's `sections` flags).
    { id: "market/transactions", t: "nav.item.market-transactions", ic: "dollar" },
  ];
  if (MARKET_AUTHOR_ROLES.has(roleId)) {
    entries.push({ id: "market/earnings", t: "nav.item.market-earnings", ic: "dollar" });
  }
  if (MARKET_ADMIN_ROLES.has(roleId)) {
    entries.push({ id: "market/admin", t: "nav.item.market-admin", ic: "settings" });
    // S-STUDIO-P1-W1 — ذخیره‌سازیِ استودیو (سهمیهٔ استاد/سقفِ سراسری/مصرف)، خواهرِ «مدیریت بازار».
    entries.push({ id: "market/admin/storage", t: "nav.item.market-admin-storage", ic: "settings" });
  }
  return entries;
};

// S-CARD فاز A W5 — «عضویتِ من» برایِ همهٔ نقش‌ها (اینجکشنِ سراسری، مثلِ بازار
// دانش). membership_operator/membership_manager از این تابع چیزی نمی‌گیرند چون
// خودشان گروهِ «عضویت» را در SIDEBAR_BY_ROLE دارند (تا هدرِ «عضویت» دوبار ساخته نشود)
// و «عضویتِ من» را مستقیماً همان‌جا کنارِ «صفِ عضویت»/«تنظیماتِ عضویت» می‌گیرند. finance_manager
// هم از W0-cfg-b گامِ ۲ صاحبِ هدرِ «عضویت» (فقط «تنظیماتِ عضویت») است — همان دلیل، همان استثنا.
const MEMBERSHIP_STAFF_ROLES: ReadonlySet<RoleId> = new Set([
  "membership_operator",
  "membership_manager",
  "finance_manager",
]);

const buildMembershipEntries = (roleId: RoleId): SidebarEntry[] => {
  if (MEMBERSHIP_STAFF_ROLES.has(roleId)) return [];
  return [
    { h: "nav.group.membership" },
    { id: "membership", t: "nav.item.membership", ic: "cert" },
    { id: "membership/directory", t: "nav.item.membership-directory", ic: "users" },
  ];
};

// S-ASSESS-W3b — «سفرِ من در سنجش» برایِ همهٔ نقش‌ها (همان الگویِ اینجکشنِ
// سراسریِ عضویت بالا: بدونِ گیتِ دادهٔ کلاینت — هر کاربری ممکن است در پروژه‌ای
// داوطلب باشد؛ صفحه با candidatesMine() خالی، پیامِ «داوطلبِ هیچ سنجشی نیستید»
// نشان می‌دهد). assessor/assessment_manager خودشان این لینک را در SIDEBAR_BY_ROLE
// کنارِ کنسولِ ارزیاب می‌گیرند (تا هدرِ «سنجش» دوبار ساخته نشود؛ الگویِ عضویت).
const ASSESS_STAFF_ROLES: ReadonlySet<RoleId> = new Set(["assessor", "assessment_manager"]);

const buildAssessCenterEntries = (roleId: RoleId): SidebarEntry[] => {
  if (ASSESS_STAFF_ROLES.has(roleId)) return [];
  return [
    { h: "nav.group.evaluationCenter" },
    { id: "assess-center/my", t: "nav.item.assess-center-my-alt2", ic: "cert" },
    // S-ASSESS-W5b — گزارشِ ر۱ منتشرشده؛ صفحه خودش حالتِ «هنوز گزارشی نیست» را نشان می‌دهد.
    { id: "assess-center/my-report", t: "nav.item.assess-center-my-report-alt2", ic: "file" },
  ];
};

// R-NAV-2/4 — the role's sidebar, CASL-filtered (spec §2.7-4) and grouped,
// with empty groups dropped. Shared by RoleSideNav (the rail/drawer) and the
// /more page (spec §3.5) so both surface exactly the same links.
export const buildSidebarGroups = (
  roleId: RoleId,
  ability: AppAbility,
): SidebarGroup[] => {
  const base = SIDEBAR_BY_ROLE[roleId] || SIDEBAR_BY_ROLE.student;
  // Inject the marketplace group at the top for every role (additive). Buyer shelf
  // is universal; author/admin entries are appended per-role (R-MARKET P5).
  const entries: SidebarEntry[] = [
    ...buildMarketEntries(roleId),
    ...buildMembershipEntries(roleId),
    ...buildAssessCenterEntries(roleId),
    ...base,
    // Every authenticated role may own a request or be assigned a workflow step.
    // Keep existing role-specific placement and add one shared entry if absent.
    ...(base.some(item => !isHeader(item) && item.id === 'service-desk') ? [] : [
      { h: 'nav.group.services' },
      { id: 'service-desk', t: 'nav.item.service-desk', ic: 'inbox' },
    ]),
  ].filter(
    (it) => isHeader(it) || !it.can || ability.can(it.can.action, it.can.subject),
  );
  const groups: SidebarGroup[] = [];
  let current: SidebarGroup | null = null;
  entries.forEach((it) => {
    if (isHeader(it)) {
      current = { h: it.h, items: [] };
      groups.push(current);
    } else if (current) {
      current.items.push(it);
    } else {
      current = { h: "", items: [it] };
      groups.push(current);
    }
  });
  return groups.filter((g) => g.items.length > 0);
};

export const RoleSideNav = ({ active, go, mode = "expanded" }: RoleSideNavProps): React.ReactElement => {
  const { role } = useRole();
  const ability = useAbility();
  // R-CHAT M4 — unread total drives the «پیام‌ها» sidebar badge (same source as
  // the global nav chip; the hook is cheap + shared-shaped so two mounts are fine).
  const { totalUnread: chatUnread } = useChatUnread();
  // S-I18N-W1a — the sidebar tables carry KEYS, not sentences; the label is
  // resolved here so a language switch re-renders the whole rail at once.
  const { t: tShell } = useTranslation("shell");
  const isMini = mode === "mini";
  const visibleGroups = buildSidebarGroups(role.id, ability);

  return (
    <aside
      className={"side-nav" + (isMini ? " side-nav-mini" : "")}
      aria-label={tShell("nav.aria.roleNav", { role: role.label })}
      data-mode={mode}
    >
      {visibleGroups.map((g, gi) => (
        <React.Fragment key={gi}>
          {/* Group headers hidden in mini mode; sr-only sibling keeps the
              grouping accessible to screen readers. */}
          {g.h && !isMini ? <h6>{tShell(g.h)}</h6> : null}
          {g.h && isMini ? <h6 className="sr-only">{tShell(g.h)}</h6> : null}
          <ul>
            {g.items.map((it) => {
              // R-CHAT M4 — show the unread total as a badge on the «پیام‌ها» item.
              const showChatBadge = it.id === "messages" && chatUnread > 0;
              const badgeText = chatUnread > 99 ? `${toFaDigits(99)}+` : toFaDigits(chatUnread);
              return (
                <li key={it.id}>
                  <a
                    href={"/" + it.id}
                    className={active === it.id ? "active" : ""}
                    aria-current={active === it.id ? "page" : undefined}
                    title={isMini ? (showChatBadge ? `${tShell(it.t)} (${badgeText})` : tShell(it.t)) : undefined}
                    onClick={(e) => {
                      e.preventDefault();
                      go(it.id);
                    }}
                    style={showChatBadge ? { position: "relative" } : undefined}
                  >
                    <Icon name={it.ic} size={14} />
                    {isMini ? <span className="sr-only">{tShell(it.t)}</span> : tShell(it.t)}
                    {showChatBadge ? (
                      <span
                        aria-label={tShell("nav.aria.unread", { count: chatUnread })}
                        style={
                          isMini
                            ? {
                                position: "absolute", top: 2, insetInlineEnd: 2,
                                minWidth: 15, height: 15, padding: "0 3px",
                                borderRadius: 8, background: "var(--accent)", color: "#fff",
                                fontSize: 8, fontWeight: 700, lineHeight: "15px", textAlign: "center",
                              }
                            : {
                                marginInlineStart: "auto",
                                minWidth: 18, height: 18, padding: "0 5px",
                                borderRadius: 9, background: "var(--accent)", color: "#fff",
                                fontSize: 10, fontWeight: 700, lineHeight: "18px", textAlign: "center",
                              }
                        }
                      >
                        {badgeText}
                      </span>
                    ) : null}
                  </a>
                </li>
              );
            })}
          </ul>
        </React.Fragment>
      ))}
    </aside>
  );
};

export default RoleSideNav;
